import React, { useCallback, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { checkAuth } from './redux/thunks/auth';
import { UserRoles } from './utils/helpers/enums';
import { saveCurrentLocation, clearCurrentLocation, getCurrentLocation } from './utils/helpers/auth';
import Spinner from 'react-bootstrap/Spinner';
import SideNav from './components/sidenav/sidenav.component';
import AuthPage from './pages/auth/auth.component';
import LogoutPage from './pages/auth/logout/logout.component';
import HoursAdminPage from './pages-admin/hours/hours.component';
import HoursPage from './pages/hours/hours.component';
import ProjectsPage from './pages-admin/projects/projects.component';
import EmployeesPage from './pages-admin/employees/employees.component';
import ExpensesPage from './pages-admin/expenses/expenses.component';
import OverviewPage from './pages-admin/overview/overview.component';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'bootstrap-daterangepicker/daterangepicker.css';
import './App.css';

const MainWrapper = ({ children }) => (<div style={{
  display: 'flex',
}}>
  <SideNav />
  <div style={{
    padding: '1rem',
    width: '100%',
    height: '100vh',
    overflowY: 'auto',
  }}>{children}</div>
</div>);

const FullScreenLoader = () => (<div style={{
  position: 'fixed',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
}}>
  <Spinner animation='border' variant='primary' style={{
    width: '3rem',
    height: '3rem',
  }} />
</div>);

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, isAuthenticating } = useSelector(state => state.entities.user);

  const isUserAdmin = userData && userData.roles && userData.roles.includes(UserRoles.admin);

  const routes = <>
    {/*PUBLIC ROUTES*/}
    {!userData ? <Routes>
      <Route path="/auth" element={<AuthPage />} />
      
      {/*  No match route, must be placed on end of routes list*/}
      <Route path="*" element={<Navigate to="/auth" />} />
    </Routes> : 
    /* PROTECTED ROUTES */
    <>
        {!isUserAdmin ? 
          <Routes>
            <Route path="/" exact element={<HoursPage />} />

            {/* Common routes */}
            <Route path="/logout" element={<LogoutPage />} />
            {/* No match route, must be placed on end of routes list*/}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes> 
          :
          <Routes>
            <Route path="/" exact element={<OverviewPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/employees" element={<EmployeesPage />} />
            <Route path="/hours" element={<HoursAdminPage />} />
            <Route path="/hours/:userId/:userEmail" element={<HoursAdminPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />

            {/* Common routes */}
            <Route path="/logout" element={<LogoutPage />} />
            {/* No match route, must be placed on end of routes list*/}
            <Route path="*" element={<Navigate to="/projects" />} />
          </Routes>
        }
    </>}
  </>;

  const getRoutes = useCallback(() => {
    return userData ? <MainWrapper>{routes}</MainWrapper> : <>{routes}</>;
  }, [userData, routes]);

  useEffect(() => {
    if (location && location.pathname) {
      saveCurrentLocation(location.pathname);
    }
    dispatch(checkAuth({}));
  }, []);

  useEffect(() => {
    if (isAuthenticating) {
      const path = getCurrentLocation();
      clearCurrentLocation();
      navigate(path || '/');
    }
  }, [isAuthenticating]);

  return <>{isAuthenticating ? <FullScreenLoader /> : getRoutes()}</>;
}

export default App;
